import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { EmptyTableState } from '@/modules/common/components/EmptyTableState';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';
import { PaymentPayMethod, PaymentStatus } from '@/types/payment';
import { PaymentGroupStage } from '@/types/payment-groups';
import { UserRole } from '@/types/user';
import { Close } from '@mui/icons-material';
import { Alert, Button } from '@mui/material';
import { ColumnFiltersState } from '@tanstack/react-table';
import {
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { enqueueSnackbar } from 'notistack';
import pluralize from 'pluralize';
import { useState } from 'react';
import { PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER } from '../../constants/payment-group-stage';
import { usePayPayments } from '../../hooks/usePayments';
import { PaymentPageStage } from '../../types';
import {
  ConfirmPaymentsFormValues,
  ConfirmPaymentsModal,
} from '../ConfirmPaymentsModal/ConfirmPaymentsModal';
import { FilterToolbar } from '../FilterToolbar';
import { PaymentInTheTable } from './ListPayments.types';
import { getListPaymentsTableTableColumns } from './ListPaymentsTable.constants';

type ListPaymentsTableProps = {
  tableOptions: Omit<MRT_TableOptions<PaymentInTheTable>, 'columns'>;
  onExport?: () => void;
  isExporting?: boolean;
  stage?: PaymentPageStage;
  hideActions?: boolean;
};

const defaultMRTOptions = getDefaultMRTOptions<PaymentInTheTable>();

export function ListPaymentsTable({
  tableOptions,
  onExport,
  isExporting = false,
  stage,
  hideActions,
}: ListPaymentsTableProps) {
  const { currentUser: { roles: userRoles = [] } = {} } =
    useCurrentUserContext() ?? {};
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { mutateAsync: payPayments } = usePayPayments();

  const getAllowedStatuses = (
    selectedStage?: PaymentPageStage,
  ): PaymentStatus[] => {
    if (!selectedStage || selectedStage === PaymentPageStage.ALL) {
      return Object.values(PaymentStatus);
    }

    const stageMapping: Record<PaymentPageStage, PaymentGroupStage> = {
      [PaymentPageStage.ALL]: PaymentGroupStage.AWAITING_PAYMENT, // This won't be used due to the check above
      [PaymentPageStage.AWAITING_PAYMENT]: PaymentGroupStage.AWAITING_PAYMENT,
      [PaymentPageStage.IN_PROGRESS]: PaymentGroupStage.IN_PROGRESS,
      [PaymentPageStage.FAILED]: PaymentGroupStage.FAILED,
      [PaymentPageStage.COMPLETED]: PaymentGroupStage.COMPLETED,
    };

    const groupStage = stageMapping[selectedStage];
    return PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER[groupStage] || [];
  };

  const allowedStatuses = getAllowedStatuses(stage);

  const handleReviewMultiple = async () => {
    const selectedRowModel = table.getSelectedRowModel();

    if (selectedRowModel.rows.length === 0) {
      return;
    }

    setConfirmationModalOpen(true);
  };

  const columns = getListPaymentsTableTableColumns({
    onPay: rowId => {
      table.resetRowSelection();
      table.setRowSelection({ [rowId]: true });
      setConfirmationModalOpen(true);
    },
    stage,
    userRoles: userRoles as UserRole[],
    hideActions,
  });

  const table = useMaterialReactTable<PaymentInTheTable>({
    ...defaultMRTOptions,
    ...tableOptions,
    columns,
    enableRowSelection: row => row.original.status === PaymentStatus.Pending,
    enableMultiRowSelection: true,
    state: {
      ...tableOptions.state,
      columnVisibility: {
        ...tableOptions.state?.columnVisibility,
        status: false, // hide status column but keep for filtering
        educationProvider: false, // hide education provider column but keep for filtering
      },
    },
    enableTopToolbar: true,
    renderToolbarInternalActions: () => {
      return <></>;
    },
    renderTopToolbar: function TopBar({ table }) {
      const { columnFilters } = table.getState();
      const numberOfRows = table.getRowCount();
      const selectedRows = table.getSelectedRowModel().rows;

      if (!numberOfRows && !columnFilters.length) {
        return <div className="empty:hidden"></div>;
      }

      const showClearFilters = columnFilters.some(columnFilter =>
        Array.isArray(columnFilter.value)
          ? columnFilter.value.length
          : columnFilter.value,
      );

      const handleColumnFiltersChange = (filters: ColumnFiltersState) => {
        table.setColumnFilters(filters);
      };

      return (
        <>
          <FilterToolbar
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            allowedStatuses={allowedStatuses}
            onExport={onExport}
            isExporting={isExporting}
            showClearFilters={showClearFilters}
            onClearFilters={() => {
              handleColumnFiltersChange([]);
              table.resetGlobalFilter();
            }}
          />

          {selectedRows.length > 0 && (
            <div className="flex gap-3 items-center p-4 w-full text-xs font-medium rounded-md bg-gray-background">
              <Alert
                icon={false}
                severity="info"
                action={
                  <>
                    <Button
                      type="button"
                      color="inherit"
                      variant="text"
                      size="small"
                      onClick={() => table.resetRowSelection()}
                      startIcon={<Close fontSize="small" />}
                    >
                      Deselect
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      variant="contained"
                      onClick={handleReviewMultiple}
                    >
                      Pay in full
                    </Button>
                  </>
                }
              >
                <span className="font-semibold">
                  {selectedRows.length}{' '}
                  {pluralize('item', selectedRows.length, false)} selected -
                  total:{' '}
                  <span className="font-bold">
                    {formatCurrencyValue(
                      selectedRows
                        .reduce(
                          (acc, row) => acc + (row.original.totalAmount ?? 0),
                          0,
                        )
                        .toString(),
                      'EUR',
                    )}
                  </span>
                </span>
              </Alert>
            </div>
          )}
        </>
      );
    },
    renderEmptyRowsFallback: () => {
      const { columnFilters } = table.getState();
      const hasFilters = columnFilters.some(columnFilter =>
        Array.isArray(columnFilter.value)
          ? columnFilter.value.length
          : columnFilter.value,
      );

      return (
        <EmptyTableState
          hasFilters={hasFilters}
          emptyStateImage="/images/no-payments.png"
          emptyStateTitle="No payments found"
          emptyStateMessage="Once you receive any payment, it will be displayed here."
        />
      );
    },
  });

  const paymentIdsToPay = table
    .getSelectedRowModel()
    .rows.map(row => row.original._id!);

  const paySelectedPayments = async (
    paymentIds: string[],
    paymentMethod: PaymentPayMethod,
  ) => {
    if (!paymentIds.length || !paymentMethod) {
      return;
    }

    try {
      const result = await payPayments({
        paymentIds: paymentIds,
        paymentMethod,
      });
      setConfirmationModalOpen(false);

      window.open(result.data.redirectUrl, '_blank');

      table.resetRowSelection();
    } catch {
      enqueueSnackbar(
        `Payment failed. We couldn't process the selected payments. Please try again.`,
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <>
      <MaterialReactTable table={table} />
      <ConfirmPaymentsModal
        paymentIds={paymentIdsToPay}
        totalToPay={table.getSelectedRowModel().rows.reduce((acc, row) => {
          return acc + (row.original.totalAmount ?? 0);
        }, 0)}
        open={isConfirmationModalOpen}
        onClose={async (formValues?: ConfirmPaymentsFormValues) => {
          if (formValues) {
            const { paymentIds, paymentMethod } = formValues;
            await paySelectedPayments(paymentIds, paymentMethod);
          }

          setConfirmationModalOpen(false);
          table.resetRowSelection();
        }}
      />
    </>
  );
}
